if (!customElements.get('product-videos-feed')) {
  class ProductVideosFeed extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-feed-track]');
      this.lightbox = this.querySelector('[data-feed-lightbox]');
      this.slides = this.lightbox ? Array.from(this.lightbox.querySelectorAll('[data-feed-slide]')) : [];
      this.currentSlide = 0;

      this.addEventListener('prev-next:prev', this.previous.bind(this));
      this.addEventListener('prev-next:next', this.next.bind(this));

      this.querySelectorAll('[data-feed-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          const index = parseInt(trigger.getAttribute('data-feed-index') || '0', 10);
          this.openLightbox(Number.isNaN(index) ? 0 : index);
        });
      });

      this.lightbox?.querySelectorAll('[data-feed-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeLightbox());
      });

      this.lightbox?.querySelector('[data-feed-prev]')?.addEventListener('click', () => {
        this.showLightboxSlide(this.currentSlide - 1);
      });

      this.lightbox?.querySelector('[data-feed-next]')?.addEventListener('click', () => {
        this.showLightboxSlide(this.currentSlide + 1);
      });

      this._onKeyDown = (event) => {
        if (!this.lightbox?.classList.contains('is-open')) return;

        if (event.key === 'Escape') {
          this.closeLightbox();
        } else if (event.key === 'ArrowLeft' && this.slides.length > 1) {
          this.showLightboxSlide(this.currentSlide - 1);
        } else if (event.key === 'ArrowRight' && this.slides.length > 1) {
          this.showLightboxSlide(this.currentSlide + 1);
        }
      };

      document.addEventListener('keydown', this._onKeyDown);
      this.setupLazyPreviews();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeyDown);
      this._lazyObserver?.disconnect();
    }

    previous(event) {
      if (!this.track) return;

      const directionFlip = window.themeVariables?.settings?.direction === 'ltr' ? 1 : -1;
      const gap = parseInt(getComputedStyle(this.track).gap || getComputedStyle(this.track).columnGap || '0', 10) || 0;

      event.target.nextElementSibling?.removeAttribute('disabled');
      event.target.toggleAttribute(
        'disabled',
        this.track.scrollLeft * directionFlip - (this.track.clientWidth + gap) <= 0
      );

      this.track.scrollBy({
        left: -(this.track.clientWidth + gap) * directionFlip,
        behavior: 'smooth'
      });
    }

    next(event) {
      if (!this.track) return;

      const directionFlip = window.themeVariables?.settings?.direction === 'ltr' ? 1 : -1;
      const gap = parseInt(getComputedStyle(this.track).gap || getComputedStyle(this.track).columnGap || '0', 10) || 0;

      event.target.previousElementSibling?.removeAttribute('disabled');
      event.target.toggleAttribute(
        'disabled',
        this.track.scrollLeft * directionFlip + (this.track.clientWidth + gap) * 2 >= this.track.scrollWidth
      );

      this.track.scrollBy({
        left: (this.track.clientWidth + gap) * directionFlip,
        behavior: 'smooth'
      });
    }

    setupLazyPreviews() {
      const previews = Array.from(this.querySelectorAll('[data-lazy-preview]'));
      if (!previews.length) return;

      const autoplay = this.dataset.autoplayPreview === 'true';

      this._lazyObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              this.pausePreview(entry.target);
              return;
            }

            this.loadPreview(entry.target, autoplay);
          });
        },
        { rootMargin: '120px', threshold: 0.25 }
      );

      previews.forEach((preview) => this._lazyObserver.observe(preview));
    }

    loadPreview(preview, autoplay) {
      if (preview.dataset.loaded === 'true') {
        if (autoplay) this.playPreview(preview);
        return;
      }

      const template = preview.querySelector('template[data-lazy-video]');
      if (!template) return;

      const node = template.content.firstElementChild?.cloneNode(true);
      if (!node) return;

      preview.querySelector('[data-lazy-poster]')?.remove();
      preview.appendChild(node);
      preview.dataset.loaded = 'true';

      if (autoplay) {
        requestAnimationFrame(() => this.playPreview(preview));
      }
    }

    playPreview(preview) {
      preview.querySelectorAll('native-video, external-video, video').forEach((element) => {
        if (typeof element.play === 'function') {
          element.play();
        } else if (element.tagName === 'VIDEO') {
          element.play().catch(() => {});
        }
      });
    }

    pausePreview(preview) {
      preview.querySelectorAll('native-video, external-video, video').forEach((element) => {
        if (typeof element.pause === 'function') {
          element.pause();
        } else if (element.tagName === 'VIDEO') {
          element.pause();
        }
      });
    }

    openLightbox(index) {
      if (!this.lightbox) return;

      this.pauseAllPreviews();
      this.lightbox.classList.add('is-open');
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('product-videos-feed-open');
      this.showLightboxSlide(index);
    }

    closeLightbox() {
      if (!this.lightbox) return;

      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('product-videos-feed-open');
      this.pauseAllSlides();

      if (this.dataset.autoplayPreview === 'true') {
        this.querySelectorAll('[data-lazy-preview][data-loaded="true"]').forEach((preview) => {
          this.playPreview(preview);
        });
      }
    }

    showLightboxSlide(index) {
      if (!this.slides.length) return;

      if (index < 0) {
        index = this.slides.length - 1;
      } else if (index >= this.slides.length) {
        index = 0;
      }

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.hidden = !isActive;
        slide.classList.toggle('is-active', isActive);

        if (!isActive) {
          this.pauseMedia(slide);
        }
      });

      this.currentSlide = index;

      const prevButton = this.lightbox.querySelector('[data-feed-prev]');
      const nextButton = this.lightbox.querySelector('[data-feed-next]');

      if (prevButton) prevButton.hidden = this.slides.length <= 1;
      if (nextButton) nextButton.hidden = this.slides.length <= 1;

      const activeSlide = this.slides[index];
      if (activeSlide) {
        this.loadSlideVideo(activeSlide);
        this.playMedia(activeSlide);
      }
    }

    loadSlideVideo(slide) {
      if (slide.dataset.loaded === 'true') return;

      const template = slide.querySelector('template[data-lazy-video]');
      if (!template) {
        slide.dataset.loaded = 'true';
        return;
      }

      const node = template.content.firstElementChild?.cloneNode(true);
      if (!node) return;

      const mount = slide.querySelector('[data-feed-slide-media]');
      if (mount) {
        mount.appendChild(node);
      }

      slide.dataset.loaded = 'true';
    }

    pauseAllPreviews() {
      this.querySelectorAll('[data-lazy-preview]').forEach((preview) => this.pausePreview(preview));
    }

    pauseAllSlides() {
      this.slides.forEach((slide) => this.pauseMedia(slide));
    }

    pauseMedia(container) {
      container.querySelectorAll('native-video, external-video, video').forEach((element) => {
        if (typeof element.pause === 'function') {
          element.pause();
        } else if (element.tagName === 'VIDEO') {
          element.pause();
        }
      });
    }

    playMedia(container) {
      container.querySelectorAll('native-video, external-video').forEach((element) => {
        element.play?.();
      });

      container.querySelectorAll('video').forEach((video) => {
        video.play().catch(() => {});
      });
    }
  }

  customElements.define('product-videos-feed', ProductVideosFeed);
}
